import { MouseEvent, useMemo, useState } from "react";
import Modal from "../../../components/Modal/Modal";
import GridIcon from "../../../assets/svg/grid-icon";
import ListViewIcon from "../../../assets/svg/list-view-icon";
import CloseIcon from "../../../assets/svg/close-icon";
import { Avatar } from "../../../components/Avatar/Avatar";
import { Tooltip } from "../../../components/Tooltip/Tooltip";

type OrganizationClient = {
  client_code: string;
  client_name: string;
  client_logo: string | null;
};

interface OrganizationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizations?: OrganizationClient[];
  isLoading?: boolean;
}

const layoutOptions = [
  { type: "grid" as const, icon: GridIcon, label: "Grid view" },
  { type: "list" as const, icon: ListViewIcon, label: "List view" },
];

export const OrganizationsModal = ({
  open,
  onOpenChange,
  organizations = [],
  isLoading = false,
}: OrganizationsModalProps) => {
  const [orgLayout, setOrgLayout] = useState<"grid" | "list">("grid");
  const hasOrganizations = organizations.length > 0;

  const handleCloseModal = () => {
    onOpenChange(false);
  };

  const handleLayoutOptionClick = (event: MouseEvent<HTMLButtonElement>) => {
    const nextLayout = event.currentTarget.dataset.layout as
      | "grid"
      | "list"
      | undefined;
    if (!nextLayout) {
      return;
    }
    setOrgLayout(nextLayout);
  };

  const contentContainerClassName = useMemo(() => {
    const base = "overflow-y-auto max-h-[250px] pb-4 pr-1";
    if (orgLayout === "grid") {
      return `flex flex-wrap gap-2 ${base}`;
    }
    return `flex flex-col gap-1 ${base}`;
  }, [orgLayout]);

  const itemClassName =
    orgLayout === "grid"
      ? "w-max flex items-center gap-1.5 px-2 py-1 bg-pure-white dark:bg-pure-white-dk rounded-md border border-border-gray dark:border-border-gray-dk"
      : "w-[97%] flex items-center gap-2 px-3 py-1 bg-pure-white dark:bg-pure-white-dk rounded-md border border-border-gray dark:border-border-gray-dk";

  const closeButton = (
    <button
      type="button"
      className="w-5 h-5 bg-gray-100 dark:bg-gray-100-dk rounded-full flex items-center justify-center"
      onClick={handleCloseModal}
      aria-label="Close modal"
    >
      <CloseIcon className="w-2 h-2 text-verba-indigo dark:text-verba-indigo-dk" />
    </button>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      showHeader={false}
      contentClassName="bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk rounded-lg w-[380px] max-h-[430px] shadow-popper-blur-effect backdrop-blur-2xl !z-[99999999]"
      contentStyle={{ top: "50%" }}
      overlayClassName="!bg-black/60 !z-[9999999]"
      bodyClassName="!px-3 !py-2"
    >
      <div className="relative flex flex-col gap-4 h-full">
        <div className="absolute top-0 right-0">{closeButton}</div>
        <div className="flex items-center justify-between mt-2 pr-8">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-heading-text dark:text-heading-text-dk">
              Organisations
            </span>
            {!isLoading && (
              <span className="text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-gray-100 dark:border-gray-100-dk rounded-md px-1.5 py-0.5">
                {organizations.length}
              </span>
            )}
          </div>

          <div className="flex items-center p-0.5 bg-gray-75 dark:bg-gray-100-dk border border-border-gray dark:border-border-gray-dk rounded-md">
            {layoutOptions.map(({ type, icon: Icon, label }) => (
              <Tooltip
                key={type}
                content={label}
                side="top"
                sideOffset={8}
                contentClassName="text-md !z-[999999999]"
              >
                <button
                  type="button"
                  data-layout={type}
                  className={`p-1 rounded ${
                    orgLayout === type
                      ? "bg-[#1a1a1a] dark:bg-fixed-white-dk"
                      : "bg-transparent"
                  }`}
                  onClick={handleLayoutOptionClick}
                >
                  <Icon
                    className={`w-4 h-4 ${
                      orgLayout === type
                        ? "text-white dark:text-[#1a1a1a]"
                        : "text-data dark:text-data-dk"
                    }`}
                  />
                </button>
              </Tooltip>
            ))}
            </div>
        </div>

        <div className={contentContainerClassName}>
          {isLoading ? (
            <>
              {Array.from({ length: orgLayout === "grid" ? 12 : 6 }).map(
                (_, index) => (
                  <div
                    key={index}
                    className={
                      orgLayout === "grid"
                        ? "w-[120px] h-[36px] rounded-md bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse"
                        : "w-full h-[40px] rounded-md bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse"
                    }
                  />
                )
              )}
            </>
          ) : hasOrganizations ? (
            organizations.map((org, index) => (
              <div key={org.client_code || index} className={itemClassName}>
                {org.client_logo ? (
                  <img
                    src={org.client_logo}
                    alt={org.client_name}
                    className="w-4 h-4 object-cover shrink-0 rounded-[3px]"
                  />
                ) : (
                  <Avatar
                    text={org.client_name}
                    size={16}
                    className="text-xxs font-medium shrink-0"
                  />
                )}
                <span className="text-md text-heading-text dark:text-heading-text-dk truncate leading-tight">
                  {org.client_name}
                </span>
              </div>
            ))
          ) : (
            <div className="w-full min-h-[260px] rounded-lg border border-border-gray dark:border-border-gray-dk flex items-center justify-center text-lg text-data dark:text-data-dk">
              -
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
